import type { MemoryState, ReviewRating } from '@/core/types';
import { DAY, MINUTE, clamp } from '@/core/util/misc';
import { L } from '@/i18n';

/**
 * Spaced-repetition memory model (FSRS-inspired).
 * Retrievability R(t) = (1 + t / (9·S))^-1, where S = stability in days.
 * The next interval targets R ≈ 0.9, which is reached after S days.
 */

export const newMemory = (): MemoryState => ({ stability: 0, difficulty: 5, lastReview: null, due: null, reps: 0, lapses: 0 });

export function retrievability(m: MemoryState, now: number): number {
  if (!m.lastReview || m.stability <= 0) return 1;
  const t = Math.max(0, (now - m.lastReview) / DAY);
  return 1 / (1 + t / (9 * m.stability));
}

const FIRST_S: Record<ReviewRating, number> = { again: 0.3, hard: 1, good: 2.5, easy: 6 };
const FIRST_D: Record<ReviewRating, number> = { again: 7, hard: 6, good: 5, easy: 3.5 };
const MULT: Record<Exclude<ReviewRating, 'again'>, number> = { hard: 0.6, good: 1, easy: 1.5 };

export function review(m: MemoryState, rating: ReviewRating, now: number): MemoryState {
  let { stability: S, difficulty: D } = m;
  if (!m.lastReview || m.reps === 0) {
    S = FIRST_S[rating];
    D = FIRST_D[rating];
  } else if (rating === 'again') {
    S = Math.max(0.2, S * 0.3);
    D = clamp(D + 1.2, 1, 10);
  } else {
    const R = retrievability(m, now);
    const growth = 1 + (11 - D) * 0.35 * Math.pow(S, -0.1) * (Math.exp((1 - R) * 2.2) - 1 + 0.35) * MULT[rating];
    S = S * Math.max(1.05, growth);
    D = clamp(D + (rating === 'hard' ? 0.4 : rating === 'good' ? -0.1 : -0.5), 1, 10);
  }
  const interval = rating === 'again' ? 10 * MINUTE : Math.max(0.5, S) * DAY;
  return {
    stability: S,
    difficulty: D,
    lastReview: now,
    due: now + interval,
    reps: m.reps + 1,
    lapses: m.lapses + (rating === 'again' ? 1 : 0),
  };
}

/** Preview the interval each rating would give (for flashcard buttons). */
export function previewIntervals(m: MemoryState, now: number): Record<ReviewRating, number> {
  const out = {} as Record<ReviewRating, number>;
  (['again', 'hard', 'good', 'easy'] as ReviewRating[]).forEach((r) => {
    const n = review(m, r, now);
    out[r] = (n.due ?? now) - now;
  });
  return out;
}

export function fmtInterval(ms: number): string {
  if (ms < 60 * MINUTE) return `${Math.max(1, Math.round(ms / MINUTE))}m`;
  if (ms < DAY) return L(`${Math.round(ms / (60 * MINUTE))}h`, `${Math.round(ms / (60 * MINUTE))} t`);
  const d = ms / DAY;
  if (d < 30) return L(`${Math.round(d)}d`, `${Math.round(d)} d`);
  if (d < 365) return L(`${Math.round(d / 30)}mo`, `${Math.round(d / 30)} mnd`);
  return L(`${(d / 365).toFixed(1)}y`, `${(d / 365).toFixed(1).replace('.', ',')} år`);
}
